import {
  AuthResponse,
  ManagerLoginResponse,
  RegisterRequest,
  LoginRequest,
  SendSmsRequest,
  CreditAnalytics,
  Dispute,
  PersonalInfoDisputeRequest,
  InquiryRemovalRequest,
  AccountDisputeRequest,
  SecurityFreezeRequest,
  MultiRoundAccountDisputeRequest,
  MultiRoundInquiryDisputeRequest,
  MultiRoundLatesDisputeRequest,
  MultiRoundPersonalInfoDisputeRequest,
  EditDisputeRequest,
  TradeLineAdviceRequest,
  TradeLineAdviceResponse,
  TradeLineAdviceOption,
  TradeLineInventoryResponse,
  RequestedTradelinesResponse,
  AdminRefreshResponse,
  SalesStatsResponse,
  CreditHistoryResponse,
  UserListResponse,
  ReportDatesResponse,
  EnrollUserToCRSResponse,
  DocumentMetadata,
  ReferralStats,
  FuelCreditsHistoryResponse,
  ReferralPairingsResponse,
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api/v1';
const FALLBACK_BACKOFFICE_VERSION =
  process.env.REACT_APP_VERSION ||
  process.env.REACT_APP_BUILD_VERSION ||
  'unknown';
//const API_BASE_URL = 'https://app-stage.creditbooster.ai/api/v1';

// Feature toggles
export const SHOW_DISPUTE_TEST_SCENARIOS = true;
export const SHOW_REGISTER_TAB = false;
interface FieldError {
  field: string;
  message: string;
}

class ApiError extends Error {
  constructor(
    public status: number, 
    message: string, 
    public details?: string,
    public fieldErrors?: FieldError[],
    public retryable?: boolean
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  private token: string | null = null;
  private managerToken: string | null = null;
  private language: string = 'en';
  private managerRefreshPromise: Promise<void> | null = null;
  private userRefreshPromise: Promise<void> | null = null;
  private backofficeVersion: string | null = null;
  private backofficeVersionPromise: Promise<string> | null = null;

  constructor() {
    // Manager token in localStorage (persists across tabs)
    this.managerToken = localStorage.getItem('managerAccessToken');
    // Client token in sessionStorage (isolated per tab)
    this.token = sessionStorage.getItem('accessToken');
    // Load language from localStorage
    this.language = localStorage.getItem('appLanguage') || 'en';
    
    console.log('[ApiService] Initialized with tokens:', {
      hasUserToken: !!this.token,
      hasManagerToken: !!this.managerToken,
    });
  }

  // Reload tokens from storage (useful after external changes)
  reloadTokens() {
    this.managerToken = localStorage.getItem('managerAccessToken');
    this.token = sessionStorage.getItem('accessToken');
    console.log('[ApiService] Tokens reloaded:', {
      hasUserToken: !!this.token,
      hasManagerToken: !!this.managerToken,
    });
  }

  setLanguage(language: string) {
    this.language = language;
  }

  getLanguage(): string {
    return this.language;
  }

  private async getBackofficeVersion(): Promise<string> {
    if (this.backofficeVersion) {
      return this.backofficeVersion;
    }

    if (this.backofficeVersionPromise) {
      return this.backofficeVersionPromise;
    }

    this.backofficeVersionPromise = fetch('/version.txt', { cache: 'no-store' })
      .then(response => {
        if (!response.ok) {
          return FALLBACK_BACKOFFICE_VERSION;
        }
        return response.text();
      })
      .then(text => {
        const version = text.trim().split(/\r?\n/)[0]?.trim();
        return version || FALLBACK_BACKOFFICE_VERSION;
      })
      .catch(() => FALLBACK_BACKOFFICE_VERSION)
      .then(version => {
        this.backofficeVersion = version;
        this.backofficeVersionPromise = null;
        return version;
      });

    return this.backofficeVersionPromise;
  }

  private async applyCommonHeaders(headers: Record<string, string>) {
    const backofficeVersion = await this.getBackofficeVersion();
    headers['x-cb-source'] = 'backoffice';
    headers['x-cb-version'] = backofficeVersion;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false // Track if this is a retry to prevent infinite loops
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Reload tokens from storage to get latest
    this.reloadTokens();

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Add common metadata headers for all API requests
    await this.applyCommonHeaders(headers);

    // Only add Content-Type for requests with body
    const method = options.method?.toUpperCase() || 'GET';
    if (['POST', 'PUT', 'PATCH'].includes(method) && options.body) {
      headers['Content-Type'] = 'application/json';
    }

    // Use manager token for manager and admin endpoints, otherwise use regular token
    const isManagerEndpoint = endpoint.startsWith('/manager/');
    const isAdminEndpoint = endpoint.startsWith('/admin/');
    const isManagerLogin = endpoint === '/manager/login';
    const isRefreshEndpoint = endpoint === '/auth/refresh-token' || endpoint === '/manager/refresh';
    
    // Auth endpoints that don't require token (login, register, send SMS, verify SMS)
    const isPublicAuthEndpoint = endpoint === '/auth/login' || 
                                  endpoint === '/auth/register' || 
                                  endpoint === '/auth/sms/send' ||
                                  endpoint === '/auth/sms/verify' ||
                                  endpoint === '/auth/login/password' ||
                                  endpoint === '/auth/reset-password';
    
    const tokenToUse = (isManagerEndpoint || isAdminEndpoint) && !isManagerLogin ? this.managerToken : this.token;

    // Add auth token if available (except for public endpoints)
    if (tokenToUse && !isManagerLogin && !isPublicAuthEndpoint) {
      headers['Authorization'] = `Bearer ${tokenToUse}`;
    }

    // Add public auth header if configured
    const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
    if (publicSecret) {
      headers['x-cb-auth'] = publicSecret;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Got 401? Try to refresh the relevant token and retry once
    if (response.status === 401 && !isRefreshEndpoint && !isRetry) {
      const isManagerOrAdmin = isManagerEndpoint || isAdminEndpoint;

      if (isManagerOrAdmin) {
        try {
          await this.refreshManagerToken();
          return await this.request<T>(endpoint, options, true);
        } catch {
          this.clearManagerToken();
          localStorage.removeItem('managerSession');
          window.dispatchEvent(new Event('managerSessionChange'));
          throw new ApiError(401, 'Manager session expired. Please log in again.');
        }
      } else {
        // User endpoint: try user refresh, then fallback to re-login via manager
        try {
          await this.refreshUserToken();
          return await this.request<T>(endpoint, options, true);
        } catch {
          const userData = sessionStorage.getItem('user');
          const hasManagerSession = !!localStorage.getItem('managerSession');
          if (hasManagerSession && userData) {
            try {
              const user = JSON.parse(userData);
              if (user.phoneNumber) {
                await this.managerLoginAsUser(user.phoneNumber);
                return await this.request<T>(endpoint, options, true);
              }
            } catch { /* re-login also failed */ }
          }
          throw new ApiError(401, 'Session expired. Please log in again.');
        }
      }
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let errorDetails: string | null = null;
      let fieldErrors: FieldError[] | undefined;
      let retryable: boolean | undefined;

      try {
        const errorData = await response.json();

        if (Array.isArray(errorData.fieldErrors)) {
          const parsed: FieldError[] = errorData.fieldErrors.filter(
            (item: unknown): item is FieldError =>
              typeof item === 'object' &&
              item !== null &&
              'field' in item &&
              'message' in item &&
              typeof (item as Record<string, unknown>).field === 'string' &&
              typeof (item as Record<string, unknown>).message === 'string'
          );
          if (parsed.length > 0) {
            fieldErrors = parsed;
          }
        }
        if (typeof errorData.retryable === 'boolean') {
          retryable = errorData.retryable;
        }

        // Handle new validation response format: { status: "INVALID", errors: [...] }
        if (errorData.status === 'INVALID' && errorData.errors) {
          errorMessage = 'Document validation failed';
          errorDetails = JSON.stringify({ errors: errorData.errors });
        } else {
          // Try multiple possible error message fields
          errorMessage = errorData.message || errorData.error || errorData.msg || errorData.detail || errorMessage;

          // Extract details for validation errors
          errorDetails = errorData.details || errorData.validationErrors || null;
        }

        // Log the full error response for debugging
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          data: errorData,
          url: response.url
        });
        console.error('Extracted error message:', errorMessage);
        console.error('Extracted error details:', errorDetails);
      } catch (parseError) {
        // If we can't parse the error response, use the status text
        errorMessage = response.statusText || errorMessage;
        console.error('Failed to parse error response:', parseError);
      }
      console.error('Creating ApiError with status:', response.status, 'message:', errorMessage);
      throw new ApiError(response.status, errorMessage, errorDetails || undefined, fieldErrors, retryable);
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    // Handle binary responses (PDF, images, etc.)
    const contentType = response.headers.get('content-type');
    if (contentType && (
      contentType.includes('application/pdf') ||
      contentType.includes('image/') ||
      contentType.includes('application/octet-stream')
    )) {
      return response.blob() as unknown as T;
    }

    return response.json();
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      sessionStorage.setItem('accessToken', token);
    } else {
      sessionStorage.removeItem('accessToken');
    }
  }

  setManagerToken(accessToken: string | null, refreshToken?: string | null) {
    this.managerToken = accessToken;
    if (accessToken) {
      localStorage.setItem('managerAccessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('managerRefreshToken', refreshToken);
      }
    } else {
      localStorage.removeItem('managerAccessToken');
      localStorage.removeItem('managerRefreshToken');
    }
  }

  clearManagerToken() {
    this.setManagerToken(null);
  }

  private async refreshManagerToken(): Promise<void> {
    if (this.managerRefreshPromise) {
      await this.managerRefreshPromise;
    } else {
      this.managerRefreshPromise = this.handleManagerTokenRefresh();
      try {
        await this.managerRefreshPromise;
      } finally {
        this.managerRefreshPromise = null;
      }
    }
  }

  private async refreshUserToken(): Promise<void> {
    if (this.userRefreshPromise) {
      await this.userRefreshPromise;
    } else {
      this.userRefreshPromise = this.handleUserTokenRefresh();
      try {
        await this.userRefreshPromise;
      } finally {
        this.userRefreshPromise = null;
      }
    }
  }

  async refreshTokensForConvex(): Promise<void> {
    const hasUserRefreshToken = !!sessionStorage.getItem('refreshToken');
    const hasManagerSession = !!localStorage.getItem('managerSession');
    const hasUserSession = !!sessionStorage.getItem('user');

    if (hasUserRefreshToken) {
      try {
        await this.refreshUserToken();
        this.reloadTokens();
        return;
      } catch { /* fall through */ }
    }

    if (hasManagerSession && hasUserSession) {
      try {
        const userData = JSON.parse(sessionStorage.getItem('user')!);
        if (userData.phoneNumber) {
          await this.managerLoginAsUser(userData.phoneNumber);
          this.reloadTokens();
          return;
        }
      } catch { /* re-login failed */ }
    }

    throw new Error('Unable to refresh user token');
  }

  private async refreshTokensForDirectFetch(): Promise<boolean> {
    const hasManagerRefreshToken = !!localStorage.getItem('managerRefreshToken');
    const hasUserRefreshToken = !!sessionStorage.getItem('refreshToken');
    const hasManagerSession = !!localStorage.getItem('managerSession');
    const hasUserSession = !!sessionStorage.getItem('user');

    let managerRefreshSuccess = false;

    if (hasManagerRefreshToken) {
      try {
        await this.refreshManagerToken();
        managerRefreshSuccess = true;
      } catch { /* fall through */ }
    }

    if (hasUserRefreshToken) {
      try {
        await this.refreshUserToken();
        this.reloadTokens();
        return true;
      } catch { /* fall through */ }
    }

    if (hasManagerSession && hasUserSession) {
      try {
        const userData = JSON.parse(sessionStorage.getItem('user')!);
        if (userData.phoneNumber) {
          await this.managerLoginAsUser(userData.phoneNumber);
          this.reloadTokens();
          return true;
        }
      } catch { /* re-login failed */ }
    }

    return managerRefreshSuccess;
  }

  private async handleUserTokenRefresh(): Promise<void> {
    const refreshToken = sessionStorage.getItem('refreshToken');
    console.log('[Auth] User token refresh - refreshToken present:', !!refreshToken);
    
    if (!refreshToken) {
      throw new Error('No user refresh token available');
    }

    console.log('[Auth] Refreshing user token...');

    const backofficeVersion = await this.getBackofficeVersion();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-cb-source': 'backoffice',
      'x-cb-version': backofficeVersion,
    };
    const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
    if (publicSecret) {
      headers['x-cb-auth'] = publicSecret;
    }
    const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      console.error('[Auth] User token refresh failed:', response.status, response.statusText);
      let errorMessage = 'User token refresh failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        console.error('[Auth] Refresh error details:', errorData);
      } catch (e) {
        console.error('[Auth] Could not parse error response');
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('[Auth] User token refresh successful, response:', {
      hasAccessToken: !!data.accessToken,
      hasRefreshToken: !!data.refreshToken
    });
    
    if (!data.accessToken) {
      console.error('[Auth] No access token in refresh response!', data);
      throw new Error('Invalid refresh response: missing accessToken');
    }
    
    this.setToken(data.accessToken);

    // Update refresh token if provided
    if (data.refreshToken) {
      sessionStorage.setItem('refreshToken', data.refreshToken);
    } else {
      console.warn('[Auth] No new refresh token provided in response');
    }
  }

  private async handleManagerTokenRefresh(): Promise<void> {
    const refreshToken = localStorage.getItem('managerRefreshToken');
    if (!refreshToken) {
      throw new Error('No manager refresh token available');
    }

    console.log('[Auth] Refreshing manager token...');

    const backofficeVersion = await this.getBackofficeVersion();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-cb-source': 'backoffice',
      'x-cb-version': backofficeVersion,
    };
    const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
    if (publicSecret) {
      headers['x-cb-auth'] = publicSecret;
    }
    const response = await fetch(`${API_BASE_URL}/manager/refresh`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || 'Manager token refresh failed';
      console.error('[Auth] Manager token refresh failed:', errorMessage);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    this.setManagerToken(data.accessToken, data.refreshToken);
    console.log('[Auth] Manager token refreshed successfully');
  }

  // Auth endpoints
  async sendSms(data: SendSmsRequest) {
    return this.request<{ success: boolean; message: string }>('/auth/sms/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      headers: {
        'x-cb-source': 'backoffice',
      },
      body: JSON.stringify(data),
    });
    this.setToken(response.accessToken);
    sessionStorage.setItem('refreshToken', response.refreshToken);
    return response;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    this.setToken(response.accessToken);
    sessionStorage.setItem('refreshToken', response.refreshToken);
    return response;
  }

  async loginWithPassword(data: { phoneNumber: string; password: string }): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login/password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async setUserPassword(data: { otp: string; newPassword: string }): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/user/set-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resetPassword(data: { phoneNumber: string; otp: string; newPassword: string }): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async logout() {
    // Full logout - clear both user and manager sessions
    this.setToken(null);
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    this.clearManagerToken();
  }

  async deleteUser(): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/auth/delete-user', {
      method: 'DELETE',
    });
  }

  logoutUser() {
    // Logout user only, keep manager session
    this.setToken(null);
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    // Keep manager tokens and session
  }

  // Manager authentication - returns manager tokens
  async managerAuthenticate(email: string, password: string): Promise<{
    success: boolean;
    accessToken: string;
    refreshToken: string;
    manager: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      isActive: boolean;
    };
  }> {
    return this.request('/manager/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  // Manager login as user via phone - uses manager token to login as a client
  async managerLoginAsUser(phoneNumber: string): Promise<ManagerLoginResponse> {
    console.log('ApiService: managerLoginAsUser called with:', { phoneNumber });
    try {
      console.log('ApiService: making request to /manager/user-login-as-manager with manager token');
      const response = await this.request<ManagerLoginResponse>('/manager/user-login-as-manager', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
      });
      console.log('ApiService: managerLoginAsUser successful, response:', response);
      this.setToken(response.accessToken);
      sessionStorage.setItem('refreshToken', response.refreshToken);
      return response;
    } catch (error) {
      console.error('ApiService: managerLoginAsUser error:', error);
      throw error;
    }
  }

  // Credit endpoints
  async getCreditAnalytics(bureau?: number): Promise<CreditAnalytics> {
    const params = new URLSearchParams();
    if (bureau) params.append('bureau', bureau.toString());
    if (this.language && this.language !== 'en') params.append('lang', this.language);
    const queryString = params.toString();
    return this.request<CreditAnalytics>(`/credit/analytics${queryString ? `?${queryString}` : ''}`);
  }

  async getCreditHistory(bureau?: number): Promise<CreditHistoryResponse> {
    const params = new URLSearchParams();
    if (bureau) params.append('bureau', bureau.toString());
    if (this.language && this.language !== 'en') params.append('lang', this.language);
    const queryString = params.toString();
    return this.request<CreditHistoryResponse>(`/credit/history${queryString ? `?${queryString}` : ''}`);
  }

  async getCreditReportPdf(
    bureau?: 1 | 3,
    disposition: 'inline' | 'attachment' = 'inline'
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (bureau) params.append('bureau', bureau.toString());
    params.append('disposition', disposition);
    const queryString = params.toString();
    return this.request<Blob>(`/credit/report/pdf${queryString ? `?${queryString}` : ''}`);
  }

  // Report History endpoints - for managers viewing user reports
  async getReportDates(userId: string, bureau?: number): Promise<ReportDatesResponse> {
    const params = new URLSearchParams();
    if (bureau) params.append('bureau', bureau.toString());
    const queryString = params.toString();
    return this.request<ReportDatesResponse>(`/manager/users/${userId}/reports${queryString ? `?${queryString}` : ''}`);
  }

  async getCreditAnalyticsByDate(userId: string, reportDate: string, bureau?: number, lang?: string): Promise<CreditAnalytics> {
    const params = new URLSearchParams();
    params.append('reportDate', reportDate);
    if (bureau) params.append('bureau', bureau.toString());
    if (lang) params.append('lang', lang);
    const queryString = params.toString();
    return this.request<CreditAnalytics>(`/manager/users/${userId}/analytics?${queryString}`);
  }

  // Dispute endpoints
  async getDisputes(): Promise<Dispute[]> {
    return this.request<Dispute[]>('/dispute/multiround');
  }

  async createPersonalInfoDispute(data: PersonalInfoDisputeRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/personal-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createInquiryRemovalDispute(data: InquiryRemovalRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/inquiry', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createAccountDispute(data: AccountDisputeRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/account', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createSecurityFreezeDispute(data: SecurityFreezeRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/freeze', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Multiround Disputes
  async createMultiRoundAccountDispute(data: MultiRoundAccountDisputeRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/multiround/account', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createMultiRoundInquiryDispute(data: MultiRoundInquiryDisputeRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/multiround/inquiry', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createMultiRoundLatesDispute(data: MultiRoundLatesDisputeRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/multiround/late', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createMultiRoundPersonalInfoDispute(data: MultiRoundPersonalInfoDisputeRequest): Promise<Dispute> {
    return this.request<Dispute>('/dispute/multiround/personal-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async editDispute(disputeId: string, data: EditDisputeRequest): Promise<Dispute> {
    return this.request<Dispute>(`/dispute/multiround/${disputeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async startNextDisputeRound(disputeId: string, round: number): Promise<Dispute> {
    return this.request<Dispute>('/dispute/multiround/next', {
      method: 'POST',
      body: JSON.stringify({ disputeId, round }),
    });
  }

  async getDisputeLetterPdf(letterId: string): Promise<Blob> {
    return this.request<Blob>(`/dispute/letter/${letterId}/pdf`);
  }

  async getAllDisputeLettersPdf(disputeId: string): Promise<Blob> {
    return this.request<Blob>(`/dispute/${disputeId}/letters/pdf`);
  }

  // Manager endpoint to view dispute letter PDF
  async getDisputeLetterPdfAsManager(letterId: string): Promise<Blob> {
    return this.request<Blob>(`/manager/letters/${letterId}/pdf`);
  }

  async getAllDisputeLettersPdfAsManager(disputeId: string): Promise<Blob> {
    return this.request<Blob>(`/manager/disputes/${disputeId}/letters/pdf`);
  }

  async updateLetterContent(letterId: string, content: string): Promise<import('../types/api').UpdateLetterContentResponse> {
    return this.request<import('../types/api').UpdateLetterContentResponse>(`/manager/letters/${letterId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  async getManagerDispute(disputeId: string): Promise<Dispute> {
    const response = await this.request<any>(`/manager/disputes/${disputeId}`);
    const raw = response.dispute || response.data || response;
    return {
      id: raw.disputeId || raw.id,
      title: raw.disputeType || raw.title || '',
      type: raw.disputeType || raw.type,
      subTypes: raw.subTypes || [],
      letters: (raw.letters || []).map((l: any) => ({
        id: l.id,
        text: l.text || '',
        bureau: l.bureau,
        round: l.round,
        lobLetterId: l.lob_letter_id || l.lobLetterId,
        lobTrackingUrl: l.lob_tracking_url || l.lobTrackingUrl,
      })),
      attachmentNames: raw.attachmentNames || [],
      supportingReason: raw.supportingReason,
      mode: raw.mode,
      createdAt: raw.createdAt,
      rawRequest: raw.rawRequest,
    };
  }

  // Tradelines endpoints
  async getTradeLineAdvice(
    data: TradeLineAdviceRequest,
    request: boolean = false
  ): Promise<TradeLineAdviceResponse> {
    const queryParams = request ? '?request=true' : '';
    return this.request<TradeLineAdviceResponse>(`/tradelines/advice${queryParams}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTradeLineAdviceOptions(): Promise<TradeLineAdviceOption[]> {
    return this.request<TradeLineAdviceOption[]>('/tradelines/advice-options');
  }

  async syncTradeLineInventory() {
    return this.request<{ success: boolean; message: string; timestamp: string }>('/admin/tradelines/sync', {
      method: 'POST',
    });
  }

  async getTradeLineInventory(): Promise<TradeLineInventoryResponse> {
    return this.request<TradeLineInventoryResponse>('/admin/tradelines/inventory');
  }

  async getRequestedTradelines(limit: number = 50, offset: number = 0): Promise<RequestedTradelinesResponse> {
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    }).toString();

    return this.request<RequestedTradelinesResponse>(`/tradelines/requested?${queryParams}`);
  }

  // Manager endpoints (use JWT token with manager role)
  async getUserList(
    page: number = 1,
    limit: number = 25,
    search: string = '',
    filters?: {
      subscription?: string;
      provider?: string;
      status?: string;
      created_from?: string;
      created_to?: string;
    }
  ): Promise<UserListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
    });
    if (filters?.subscription) params.append('subscription', filters.subscription);
    if (filters?.provider) params.append('provider', filters.provider);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.created_from) params.append('created_from', filters.created_from);
    if (filters?.created_to) params.append('created_to', filters.created_to);
    return this.request<UserListResponse>(`/manager/users?${params}`);
  }

  // Admin endpoints (use JWT token with admin role)
  async getSalesStats(identityIqEntity?: string): Promise<SalesStatsResponse> {
    const params = identityIqEntity ? `?identityIqEntity=${encodeURIComponent(identityIqEntity)}` : '';
    return this.request<SalesStatsResponse>(`/admin/sales-stats${params}`);
  }

  async refreshAllReports(): Promise<AdminRefreshResponse> {
    return this.request<AdminRefreshResponse>('/admin/refresh-reports', {
      method: 'POST',
    });
  }

  async refreshUserReport(userId: string, bureauCount: 1 | 3 = 3): Promise<AdminRefreshResponse> {
    return this.request<AdminRefreshResponse>(`/admin/refresh-reports/${userId}?bureau=${bureauCount}`, {
      method: 'POST',
    });
  }

  async generateIdentityIQLink(userId: string): Promise<{
    success: boolean;
    message: string;
    link: string;
  }> {
    return this.request<{
      success: boolean;
      message: string;
      link: string;
    }>(`/manager/identityiq-link/${userId}`);
  }

  async sendPhysicalLetters(disputeId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      lettersCreated: number;
      lobLetterId: string;
      trackingUrl: string;
      expectedDeliveryDate: string;
    };
  }> {
    return this.request(`/dispute/${disputeId}/send-physical`, {
      method: 'POST',
    });
  }

  async sendForApproval(disputeId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      lettersScheduled: number;
      status: string;
    };
  }> {
    return this.request(`/dispute/${disputeId}/send-for-approval`, {
      method: 'POST',
    });
  }

  async getDisputesForReview(page: number = 1, limit: number = 50, slaStatus?: string): Promise<{
    disputes: Array<{
      disputeId: string;
      userId: string;
      disputeType: string;
      disputeStatus: string;
      attachmentNames: string[];
      createdAt: number;
      updatedAt?: number;
      sla?: {
        targetHours: number;
        hoursSinceCreation: number;
        hoursSinceLastUpdate: number;
        status: 'on_track' | 'at_risk' | 'overdue';
      };
      user: { firstName: string; lastName: string; phoneNumber: string; email: string };
      documentReviews: Array<{
        documentId: string;
        documentName: string;
        status: string;
        reviewComments: Array<{
          id: string;
          comment: string;
          status: 'open' | 'closed';
          created_at: number;
          closed_at?: number;
          reviewer_id: string;
        }>;
      }>;
      letters: Array<{
        letterId: string;
        bureau: string;
        round?: number;
        status: string;
        statusHistory: Array<{ status: string; timestamp: number }>;
        lobTrackingUrl?: string;
        lobExpectedDeliveryDate?: string;
      }>;
    }>;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (slaStatus) params.append('sla_status', slaStatus);
    return this.request(`/manager/disputes/review?${params}`, {
      method: 'GET',
    });
  }

  async sendScheduledLetters(disputeId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      disputeId: string;
      lettersCreated: number;
      results: Array<{
        bureau: string;
        letterId: string;
        lobLetterId: string;
        success: boolean;
        error?: string;
      }>;
    };
  }> {
    return this.request(`/manager/disputes/${disputeId}/send-physical`, {
      method: 'POST',
    });
  }

  // Document endpoints
  async uploadDocument(file: File, documentName: string, isRetry: boolean = false): Promise<{
    success: boolean;
    message: string;
    data: {
      id: string;
      userId: string;
      documentName: string;
      fileSize: number;
      mimeType: string;
      version: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    this.reloadTokens();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentName', documentName);

    const url = `${API_BASE_URL}/documents/upload`;
    const headers: Record<string, string> = {};
    await this.applyCommonHeaders(headers);

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
    if (publicSecret) {
      headers['x-cb-auth'] = publicSecret;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401 && !isRetry) {
      console.log('[Auth] 401 on uploadDocument - attempting token refresh');
      const refreshed = await this.refreshTokensForDirectFetch();
      if (refreshed) {
        return this.uploadDocument(file, documentName, true);
      }
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let errorDetails: string | null = null;
      
      try {
        const errorData = await response.json();
        
        if (errorData.status === 'INVALID' && errorData.errors) {
          errorMessage = 'Document validation failed';
          errorDetails = JSON.stringify({ errors: errorData.errors });
        } else {
          errorMessage = errorData.message || errorData.error || errorData.msg || errorData.detail || errorMessage;
          errorDetails = errorData.details || errorData.validationErrors || null;
        }
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw new ApiError(response.status, errorMessage, errorDetails || undefined);
    }

    return response.json();
  }

  async uploadDocumentForDispute(file: File, documentName: string, disputeId: string, isRetry: boolean = false): Promise<{
    success: boolean;
    message: string;
    data: {
      id: string;
      userId: string;
      documentName: string;
      fileSize: number;
      mimeType: string;
      version: number;
      createdAt: string;
      updatedAt: string;
    };
  }> {
    this.reloadTokens();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentName', documentName);

    const url = `${API_BASE_URL}/documents/upload?disputeId=${encodeURIComponent(disputeId)}`;
    const headers: Record<string, string> = {};
    await this.applyCommonHeaders(headers);

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
    if (publicSecret) {
      headers['x-cb-auth'] = publicSecret;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401 && !isRetry) {
      console.log('[Auth] 401 on uploadDocumentForDispute - attempting token refresh');
      const refreshed = await this.refreshTokensForDirectFetch();
      if (refreshed) {
        return this.uploadDocumentForDispute(file, documentName, disputeId, true);
      }
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let errorDetails: string | null = null;
      
      try {
        const errorData = await response.json();
        
        if (errorData.status === 'INVALID' && errorData.errors) {
          errorMessage = 'Document validation failed';
          errorDetails = JSON.stringify({ errors: errorData.errors });
        } else {
          errorMessage = errorData.message || errorData.error || errorData.msg || errorData.detail || errorMessage;
          errorDetails = errorData.details || errorData.validationErrors || null;
        }
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw new ApiError(response.status, errorMessage, errorDetails || undefined);
    }

    return response.json();
  }

  async listDocuments(): Promise<DocumentMetadata[]> {
    const response = await this.request<{
      success: boolean;
      data: DocumentMetadata[];
    }>('/documents');

    return response.data || [];
  }

  async downloadDocument(documentId: string, bustCache?: boolean): Promise<Blob> {
    const cacheBuster = bustCache ? `?t=${Date.now()}` : '';
    return this.request<Blob>(`/documents/${documentId}/download${cacheBuster}`);
  }

  async deleteDocument(documentId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async getUserDocumentsAsManager(userId: string): Promise<DocumentMetadata[]> {
    const response = await this.request<{
      success: boolean;
      documents: DocumentMetadata[];
    }>(`/manager/users/${userId}/documents`);

    return response.documents || [];
  }

  async reviewDocument(documentId: string, status: 'approved' | 'rejected', comment?: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/manager/documents/${documentId}/review`, {
      method: 'POST',
      body: JSON.stringify({ status, ...(comment && { comment }) }),
    });
  }

  async validateDocument(file: File, documentName: string, isRetry: boolean = false): Promise<{
    status: 'VALID' | 'INVALID';
    errors: string[];
    notes?: string;
    expirationDate?: string;
  }> {
    // Reload tokens
    this.reloadTokens();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentName', documentName);

    const url = `${API_BASE_URL}/documents/validate`;

    const headers: Record<string, string> = {};
    await this.applyCommonHeaders(headers);
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    // Do NOT set Content-Type - let browser set it with boundary

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401 && !isRetry) {
      const refreshed = await this.refreshTokensForDirectFetch();
      if (refreshed) {
        return this.validateDocument(file, documentName, true);
      }
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.message || 'Failed to validate document',
        errorData
      );
    }

    return response.json();
  }

  async voiceHelper(formData: FormData, isRetry: boolean = false): Promise<{ answer: string } | { success: false; message: string; error?: string }> {
    // Reload tokens
    this.reloadTokens();

    const url = `${API_BASE_URL}/credit/voice-helper`;

    const headers: Record<string, string> = {};
    await this.applyCommonHeaders(headers);

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData, // Don't set Content-Type, let browser set it with boundary
    });

    if (response.status === 401 && !isRetry) {
      const refreshed = await this.refreshTokensForDirectFetch();
      if (refreshed) {
        return this.voiceHelper(formData, true);
      }
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(response.status, errorData.message || 'Voice helper request failed');
    }

    return response.json();
  }

  async toggleMaxPlanAccess(userId: string, enabled: boolean): Promise<{ success: boolean; message: string; userId: string; user: any }> {
    return this.request(`/manager/toggle-max-plan/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  }

  async getCurrentUser(): Promise<any> {
    return this.request('/user/info', {
      method: 'GET',
    });
  }

  async createSubscriptionStripeInvoiceLink(data: {
    planId: 'starter' | 'pro' | 'max';
    billingPeriod?: 'monthly' | 'annually';
  }): Promise<{
    success: boolean;
    checkoutUrl: string;
    stripeSubscriptionId: string;
    stripeInvoiceId: string;
    stripeProductId: string;
    stripePriceId: string;
    period?: 'month' | 'year';
    billingPeriod?: 'monthly' | 'annually';
  }> {
    return this.request('/subscription/stripe-invoice-link', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getIdentityIQToken(): Promise<{
    success: boolean;
    token: string;
    membershipNo: string;
  }> {
    return this.request('/auth/identityiq-token', {
      method: 'GET',
    });
  }

  // Verification endpoints
  async getVerificationQuestions(): Promise<import('../types/api').GetVerificationQuestionsResponse> {
    return this.request('/verification/questions', {
      method: 'GET',
    });
  }

  async submitVerificationAnswers(
    answers: string[]
  ): Promise<import('../types/api').SubmitVerificationAnswersResponse> {
    return this.request('/verification/submit-answers', {
      method: 'POST',
      body: JSON.stringify({ answers }),
    });
  }

  // CRS Identity Verification
  async verifyCRSIdentity(): Promise<import('../types/api').CRSIdentityVerificationResponse> {
    return this.request('/verification/identity', {
      method: 'POST',
    });
  }

  async saveCreditReportHtml(html: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request<{
      success: boolean;
      message: string;
    }>('/credit/report/html', {
      method: 'POST',
      body: JSON.stringify({ html }),
    });
  }

  async registerCRSUser(userData: {
    email: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    ssn: string;
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    phoneNumber: string;
    referralId?: string;
    goal?: string;
    goalTargetPeriod?: string;
  }, isRetry: boolean = false): Promise<{
    success: boolean;
    message: string;
    user: any;
    accessToken: string;
    refreshToken?: string;
    verification?: {
      idpass: boolean;
      requiresSMFA: boolean;
      smfaToken?: string;
      message?: string;
      smsMessage?: string;
    };
  }> {
    this.reloadTokens();
    const url = `${API_BASE_URL}/auth/register`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-credit-provider': 'crs',
    };
    await this.applyCommonHeaders(headers);

    if (this.managerToken) {
      headers['Authorization'] = `Bearer ${this.managerToken}`;
    }

    const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
    if (publicSecret) {
      headers['x-cb-auth'] = publicSecret;
    }

    const body = {
      ...userData,
      goal: userData.goal || 'improve_score',
      goalTargetPeriod: userData.goalTargetPeriod || '6_months',
      smsCode: '777777',
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (response.status === 401 && !isRetry) {
      console.log('[Auth] 401 on registerCRSUser - attempting token refresh');
      const refreshed = await this.refreshTokensForDirectFetch();
      if (refreshed) {
        return this.registerCRSUser(userData, true);
      }
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.message || errorData.error || 'Failed to register CRS user',
        errorData.details,
        errorData.fieldErrors,
        errorData.retryable
      );
    }

    return response.json();
  }

  async registerIdentityIQUser(userData: {
    email: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    ssn: string;
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    phoneNumber: string;
    referralId?: string;
    goal?: string;
    goalTargetPeriod?: string;
  }, isRetry: boolean = false): Promise<{
    success: boolean;
    message: string;
    user: any;
    accessToken: string;
    refreshToken?: string;
  }> {
    this.reloadTokens();
    const url = `${API_BASE_URL}/auth/register`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-credit-provider': 'identityiq',
    };
    await this.applyCommonHeaders(headers);

    if (this.managerToken) {
      headers['Authorization'] = `Bearer ${this.managerToken}`;
    }

    const publicSecret = process.env.REACT_APP_PUBLIC_SECRET;
    if (publicSecret) {
      headers['x-cb-auth'] = publicSecret;
    }

    const body = {
      ...userData,
      goal: userData.goal || 'improve_score',
      goalTargetPeriod: userData.goalTargetPeriod || '6_months',
      smsCode: '777777',
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (response.status === 401 && !isRetry) {
      console.log('[Auth] 401 on registerIdentityIQUser - attempting token refresh');
      const refreshed = await this.refreshTokensForDirectFetch();
      if (refreshed) {
        return this.registerIdentityIQUser(userData, true);
      }
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.message || errorData.error || 'Failed to register IdentityIQ user',
        errorData.details,
        errorData.fieldErrors,
        errorData.retryable
      );
    }

    return response.json();
  }

  // CRS Alternative Verification Methods
  async crsVerifyIdentity(data: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    ssn: string;
    dateOfBirth: string;
    address: {
      street1: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
    };
  }): Promise<{
    success: boolean;
    idpass: boolean;
    requiresSMFA?: boolean;
    mtoken?: string;
    message: string;
  }> {
    return this.request('/alt/verify-identity', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async crsSendSMFA(mtoken: string): Promise<{
    success: boolean;
    smfaToken: string;
    message: string;
  }> {
    return this.request('/alt/send-smfa', {
      method: 'POST',
      body: JSON.stringify({ mtoken }),
    });
  }

  async crsCheckSMFAStatus(smfaToken: string): Promise<{
    success: boolean;
    verified: boolean;
    idpass: boolean;
    message: string;
  }> {
    return this.request('/alt/check-smfa-status', {
      method: 'POST',
      body: JSON.stringify({ smfaToken }),
    });
  }

  // Force refresh credit data for a CRS user
  async forceRefreshCreditData(userId: string): Promise<void> {
    return this.request<void>(`/admin/force-refresh/${userId}`, {
      method: 'POST',
    });
  }

  // Enroll existing user to CRS
  async enrollUserToCRS(userId: string): Promise<EnrollUserToCRSResponse> {
    return this.request<EnrollUserToCRSResponse>('/admin/migrate-user-to-crs', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  // Migrate IdentityIQ user to CRS
  async migrateUserToCRS(userId: string): Promise<{
    success: boolean;
    message: string;
    user?: any;
  }> {
    return this.request('/admin/migrate-user-to-crs', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  // Delete dispute by admin
  async deleteDispute(disputeId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/admin/disputes/${disputeId}`, {
      method: 'DELETE',
    });
  }

  // Delete a specific letter from a dispute by admin
  async deleteDisputeLetter(disputeId: string, letterId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/admin/disputes/${disputeId}/letters/${letterId}`, {
      method: 'DELETE',
    });
  }

  // Get full SSN for a user
  async getUserSSN(userId: string): Promise<{
    success: boolean;
    data: {
      userId: string;
      ssn: string;
    };
  }> {
    return this.request(`/admin/users/${userId}/ssn`, {
      method: 'GET',
    });
  }

  async getAdminUser(userId: string): Promise<any> {
    return this.request(`/admin/users/${userId}`, {
      method: 'GET',
    });
  }

  async getConvexToken(): Promise<{
    success: boolean;
    token: string;
    expiresAt: number;
  }> {
    return this.request('/auth/convex-token', {
      method: 'GET',
    });
  }

  async getScoreDeltas(userId: string): Promise<{
    success: boolean;
    deltas: Array<{
      bureau: string;
      currentScore: number;
      previousScore: number;
      delta: number;
      currentDate: string;
      previousDate: string;
    }>;
    currentReportDate: string;
    previousReportDate: string;
  }> {
    return this.request(`/credit/score-delta?userId=${userId}`, {
      method: 'GET',
    });
  }

  // Tradeline Orders - Admin
  async getTradelineOrders(params?: {
    status?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    per_page?: number;
    search?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.search) queryParams.append('search', params.search);
    
    const query = queryParams.toString();
    return this.request(`/admin/tradeline-orders${query ? `?${query}` : ''}`, {
      method: 'GET',
    });
  }

  async getTradelineOrder(orderId: string): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}`, {
      method: 'GET',
    });
  }

  async markTradelineOrderLinesOrdered(orderId: string, providerOrderId?: string): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}/mark-lines-ordered`, {
      method: 'POST',
      body: JSON.stringify({ provider_order_id: providerOrderId }),
    });
  }

  async markTradelineOrderLinesAdded(orderId: string, bankAccountLast4?: string): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}/mark-lines-added`, {
      method: 'POST',
      body: JSON.stringify({ bank_account_last4: bankAccountLast4 }),
    });
  }

  async markTradelineOrderLinesPosted(orderId: string): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}/mark-lines-posted`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async addTradelineOrderNote(orderId: string, content: string): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}/add-note`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async cancelTradelineOrder(orderId: string, reason: string): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async refundTradelineOrder(orderId: string, reason: string): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}/refund`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async replaceTradelineOrderItem(
    orderId: string,
    itemIndex: number,
    newItem: {
      tradeline_inventory_id: string;
      name: string;
      age_years: number;
      credit_limit: number;
      price: number;
      provider: string;
      quantity: number;
    },
    reason: string
  ): Promise<any> {
    return this.request(`/admin/tradeline-orders/${orderId}/replace-item`, {
      method: 'POST',
      body: JSON.stringify({ item_index: itemIndex, new_item: newItem, reason }),
    });
  }

  // User Notes
  async getUserNotes(userId: string, limit: number = 50, offset: number = 0): Promise<any> {
    return this.request(`/admin/users/${userId}/notes?limit=${limit}&offset=${offset}`, {
      method: 'GET',
    });
  }

  async createUserNote(userId: string, content: string): Promise<any> {
    return this.request(`/admin/users/${userId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async updateUserNote(noteId: string, content: string): Promise<any> {
    return this.request(`/admin/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  // Referral + Fuel Credits
  async getReferralStats(userId: string): Promise<ReferralStats> {
    return this.request(`/admin/users/${userId}/referral/stats`, {
      method: 'GET',
    });
  }

  async getReferralPairings(userId: string): Promise<ReferralPairingsResponse> {
    return this.request(`/admin/users/${userId}/referral/pairings`, {
      method: 'GET',
    });
  }

  async getReferralCode(userId: string): Promise<{ code: string; invite_url: string }> {
    return this.request(`/admin/users/${userId}/referral/code`, {
      method: 'GET',
    });
  }

  async simulateReferralPurchase(userId: string): Promise<{ success: boolean; balance_before: number; balance_after: number; message: string }> {
    return this.request(`/admin/users/${userId}/referral/simulate-purchase`, {
      method: 'POST',
    });
  }

  async simulateTrialPurchase(userId: string): Promise<import('../types/api').SimulateTrialPurchaseResponse> {
    return this.request(`/admin/users/${userId}/simulate-trial-purchase`, {
      method: 'POST',
    });
  }

  async claimFuelCredit(entryId: string): Promise<{ success: boolean; claimed: boolean; message: string }> {
    return this.request(`/admin/fuel-credits/${entryId}/claim`, {
      method: 'POST',
    });
  }

  async getFuelCreditsHistory(
    userId: string,
    limit = 20,
    offset = 0
  ): Promise<FuelCreditsHistoryResponse> {
    return this.request(
      `/admin/users/${userId}/fuel-credits/history?limit=${limit}&offset=${offset}`,
      { method: 'GET' }
    );
  }

  // Tradeline Orders - User
  async createTradelineOrder(data: any): Promise<any> {
    return this.request('/tradelines/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getUserTradelineOrders(): Promise<any> {
    return this.request('/tradelines/orders', {
      method: 'GET',
    });
  }

  async getUserTradelineOrder(orderId: string): Promise<any> {
    return this.request(`/tradelines/orders/${orderId}`, {
      method: 'GET',
    });
  }

  async extendTradelineOrder(orderId: string): Promise<any> {
    return this.request(`/tradelines/orders/${orderId}/extend`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }
}

export const apiService = new ApiService();
export { ApiError };
export type { FieldError };
